/**
 * Overview — landing page after login (PHASE2_SPEC §4.1).
 *
 * Layout (top → bottom):
 *   Row 1: 4 KPI tiles (banks · loans · deposits · system NPA %)
 *   Row 2: 3 KPI tiles (NPA · branches · customers)
 *   Row 3: Two donut charts side-by-side (deposit mix · NPA mix)
 *   Row 4: Trend line — hidden behind <EmptyTrendNotice/> when < 2 snapshots
 *   Row 5: Three ranked tables (top by loan, deposit, NPA %)
 *
 * Click a ranked row → /banks/:orgId.
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import BankBadge from '../../components/BankBadge';
import Card from '../../components/Card';
import Donut, { DonutLegend, type DonutDatum } from '../../components/Donut';
import EmptyState from '../../components/EmptyState';
import EmptyTrendNotice from '../../components/EmptyTrendNotice';
import ErrorBanner from '../../components/ErrorBanner';
import KpiTile from '../../components/KpiTile';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import RankedList, { type RankedRow } from '../../components/RankedList';
import TrendChart, { type TrendSeries } from '../../components/TrendChart';
import { formatDate } from '../../helpers/dateFormat';
import { npaFlag } from '../../helpers/flag';
import { formatAbsCrore, formatCount, formatCrore, formatPct } from '../../helpers/format';
import { DEPOSIT_COLORS, NPA_COLORS, TREND_COLORS } from '../../helpers/palette';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchOverview, selectOverview } from '../../redux/slices/dashboard';
import type {
	OverviewTopByDeposit,
	OverviewTopByLoan,
	OverviewTopByNpaPct,
} from '../../type/api';

const TREND_SERIES: TrendSeries[] = [
	{ dataKey: 'loanCr', name: 'Loans', color: TREND_COLORS.loanCr },
	{ dataKey: 'depositCr', name: 'Deposits', color: TREND_COLORS.depositCr },
	{ dataKey: 'npaCr', name: 'NPA', color: TREND_COLORS.npaCr },
];

export default function OverviewPage() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const cell = useAppSelector(selectOverview);

	useEffect(() => {
		if (cell.status === 'idle') {
			dispatch(fetchOverview());
		}
	}, [cell.status, dispatch]);

	const goBank = (orgId: number) => navigate(`/banks/${orgId}`);

	const handleRetry = () => dispatch(fetchOverview());

	if (cell.status === 'loading' && !cell.data) {
		return (
			<div className="page-body">
				<div className="page-head">
					<div>
						<h1 className="page-title">Overview</h1>
						<div className="page-sub">All-banks summary KPIs and rankings.</div>
					</div>
				</div>
				<Card>
					<LoadingSkeleton rows={4} columns={4} />
				</Card>
			</div>
		);
	}

	if (cell.status === 'error') {
		return (
			<div className="page-body">
				<div className="page-head">
					<div>
						<h1 className="page-title">Overview</h1>
					</div>
				</div>
				<ErrorBanner message={cell.error || 'Failed to load overview'} onRetry={handleRetry} />
			</div>
		);
	}

	const data = cell.data;
	if (!data) {
		return (
			<div className="page-body">
				<div className="page-head">
					<div>
						<h1 className="page-title">Overview</h1>
					</div>
				</div>
				<Card>
					<EmptyState title="No data" hint="Once the ETL completes a snapshot, this page will populate." />
				</Card>
			</div>
		);
	}

	const totals = data.totals;
	const depositMixData: DonutDatum[] = [
		{ name: 'Savings', value: data.depositMix.savingsCr, color: DEPOSIT_COLORS.savings },
		{ name: 'FD', value: data.depositMix.fdCr, color: DEPOSIT_COLORS.fd },
		{ name: 'RD', value: data.depositMix.rdCr, color: DEPOSIT_COLORS.rd },
		{ name: 'Current', value: data.depositMix.currentCr, color: DEPOSIT_COLORS.current },
	];
	const npaMixData: DonutDatum[] = [
		{ name: 'Standard', value: data.npaMix.standardCr, color: NPA_COLORS.standard },
		{ name: 'Sub-Standard', value: data.npaMix.subStandardCr, color: NPA_COLORS.subStandard },
		{ name: 'Doubtful', value: data.npaMix.doubtfulCr, color: NPA_COLORS.doubtful },
		{ name: 'Loss', value: data.npaMix.lossCr, color: NPA_COLORS.loss },
	];
	const trend = Array.isArray(data.trend) ? data.trend : [];

	const topByLoan = Array.isArray(data.topByLoan) ? data.topByLoan : [];
	const topByDeposit = Array.isArray(data.topByDeposit) ? data.topByDeposit : [];
	const topByNpaPct = Array.isArray(data.topByNpaPct) ? data.topByNpaPct : [];

	const loanRows: RankedRow<OverviewTopByLoan>[] = topByLoan.slice(0, 10).map((r) => ({
		key: r.orgId,
		label: <BankBadge orgId={r.orgId} orgName={r.orgName} />,
		value: formatAbsCrore(r.loanCr),
		rightHint: `NPA ${formatPct(r.npaPct)}`,
		row: r,
	}));

	const depositRows: RankedRow<OverviewTopByDeposit>[] = topByDeposit.slice(0, 10).map((r) => ({
		key: r.orgId,
		label: <BankBadge orgId={r.orgId} orgName={r.orgName} />,
		value: formatAbsCrore(r.depositCr),
		row: r,
	}));

	const npaRows: RankedRow<OverviewTopByNpaPct>[] = topByNpaPct.slice(0, 10).map((r) => ({
		key: r.orgId,
		label: <BankBadge orgId={r.orgId} orgName={r.orgName} />,
		value: formatPct(r.npaPct),
		rightHint: formatAbsCrore(r.loanCr),
		row: r,
	}));

	return (
		<div className="page-body">
			<div className="page-head">
				<div>
					<h1 className="page-title">Overview</h1>
					<div className="page-sub">
						All-banks summary KPIs and rankings.
						{data.asOf && (
							<>
								{' · As of '}
								<span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>
									{formatDate(data.asOf)}
								</span>
							</>
						)}
					</div>
				</div>
				<button type="button" className="btn ghost" onClick={handleRetry} disabled={cell.status === 'loading'}>
					Refresh
				</button>
			</div>

			<div className="grid cols-4">
				<KpiTile label="Banks Reporting" value={formatCount(totals.banksReporting)} sub={`${formatCount(totals.branches)} branches`} />
				<KpiTile label="Total Loans" value={formatCrore(totals.totalLoanAmountCr, 2)} sub={`${formatCount(totals.totalLoanCount)} accounts`} />
				<KpiTile label="Total Deposits" value={formatCrore(totals.totalDepositBalanceCr, 2)} sub={`${formatCount(totals.totalDepositCount)} accounts`} />
				<KpiTile
					label="System NPA %"
					value={formatPct(totals.systemNpaPct)}
					flag={npaFlag(totals.systemNpaPct)}
					flagVariant="npa"
					sub="RBI norm ≤ 7%"
					primary
				/>
			</div>

			<div className="grid cols-3">
				<KpiTile label="Total NPA" value={formatCrore(totals.totalNpaAmountCr, 2)} sub={`${formatCount(totals.totalNpaCount)} accounts`} />
				<KpiTile label="Total Branches" value={formatCount(totals.branches)} sub="Across reporting banks" />
				<KpiTile
					label="Customers"
					value={formatCount(totals.totalUniqueCustomers)}
					sub={`${formatCount(totals.totalKycAccounts)} KYC accounts`}
				/>
			</div>

			<div className="grid cols-2">
				<Card title="Deposit mix">
					<div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 18, alignItems: 'center' }}>
						<Donut data={depositMixData} />
						<DonutLegend data={depositMixData} />
					</div>
				</Card>
				<Card title="NPA mix">
					<div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 18, alignItems: 'center' }}>
						<Donut data={npaMixData} />
						<DonutLegend data={npaMixData} />
					</div>
				</Card>
			</div>

			<Card title="Loans / Deposits / NPA — trend (₹ Cr)">
				{trend.length < 2 ? (
					<EmptyTrendNotice asOf={data.asOf} />
				) : (
					<TrendChart
						data={trend as unknown as Record<string, number | string>[]}
						xKey="date"
						series={TREND_SERIES}
						asOf={data.asOf}
						valueFormatter={(v) => formatCrore(v, 1)}
					/>
				)}
			</Card>

			<div className="grid cols-3">
				<Card title="Top 10 by Loan Amount">
					<RankedList<OverviewTopByLoan>
						rows={loanRows}
						onRowClick={(r) => goBank(r.orgId)}
						emptyHint="No banks ranked yet."
					/>
				</Card>
				<Card title="Top 10 by Deposit Balance">
					<RankedList<OverviewTopByDeposit>
						rows={depositRows}
						onRowClick={(r) => goBank(r.orgId)}
						emptyHint="No banks ranked yet."
					/>
				</Card>
				<Card title="Top 10 by NPA %">
					<RankedList<OverviewTopByNpaPct>
						rows={npaRows}
						onRowClick={(r) => goBank(r.orgId)}
						emptyHint="No banks ranked yet."
					/>
				</Card>
			</div>
		</div>
	);
}
